import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useCustomerContacts } from "@/hooks/useCustomerContacts";
import { toast } from "sonner";
import { Send, Copy, Loader2, ExternalLink } from "lucide-react";
import { Estimate } from "@/hooks/useEstimates";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";

const fmt = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  estimate: Estimate | null;
  onSent: () => void;
}

export const SendEstimateDialog = ({ open, onOpenChange, estimate, onSent }: Props) => {
  const { data: companyProfile } = useCompanyProfile();
  const [sendTo, setSendTo] = useState<"primary" | "all" | "custom">("primary");
  const [customEmail, setCustomEmail] = useState("");
  const [contactName, setContactName] = useState("");
  const [message, setMessage] = useState("");
  const [expiryDays, setExpiryDays] = useState("30");
  const [sending, setSending] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  const { data: contacts } = useCustomerContacts(estimate?.customer_id ?? undefined);
  const primaryContact = contacts?.find((c) => c.is_primary);
  const estimateContacts = contacts?.filter((c) => c.receives_estimates) ?? [];

  useEffect(() => {
    if (open) {
      setShareUrl(null);
      setSending(false);
      setMessage("");
      if (primaryContact) {
        setContactName(primaryContact.name);
      }
    }
  }, [open, primaryContact]);

  if (!estimate) return null;

  const getTargetEmail = () => {
    if (sendTo === "primary") return primaryContact?.email || "";
    if (sendTo === "custom") return customEmail;
    return estimateContacts.map((c) => c.email).filter(Boolean).join(", ");
  };

  const getTargetName = () => {
    if (sendTo === "primary") return primaryContact?.name || contactName;
    if (sendTo === "custom") return contactName;
    return contactName || "Customer";
  };

  const handleSend = async () => {
    const toEmail = getTargetEmail();
    const toName = getTargetName();
    if (!toEmail || !toName) {
      toast.error("Please provide a recipient email and name");
      return;
    }

    setSending(true);
    try {
      // Generate token
      const { data: token, error: tokenErr } = await supabase.rpc("generate_estimate_token", {
        p_estimate_id: estimate.id,
      });
      if (tokenErr) throw tokenErr;

      // Update sent_to fields
      await supabase.from("estimates").update({
        sent_to_email: toEmail,
        sent_to_name: toName,
      } as any).eq("id", estimate.id);

      const appUrl = window.location.origin;
      const url = `${appUrl}/estimate/${token}`;
      setShareUrl(url);

      // Try to send email via edge function (best-effort)
      try {
        const { error: fnErr } = await supabase.functions.invoke("send-estimate-email", {
          body: {
            to: toEmail,
            toName,
            estimateId: estimate.id,
            estimateNumber: `EST-${estimate.estimate_number}`,
            totalAmount: fmt(estimate.total_amount),
            shareUrl: url,
            personalMessage: message || null,
            expiryDate: new Date(Date.now() + parseInt(expiryDays) * 86400000).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
            companyName: companyProfile?.legal_name ?? companyProfile?.name ?? "Company",
            jobType: (estimate as any).job_type || "construction",
          },
        });
        if (fnErr) console.error("send-estimate-email error:", fnErr);
      } catch (e) {
        console.error("send-estimate-email threw:", e);
      }

      toast.success("Estimate sent! Share the link with your customer.");
      onSent();
    } catch (err: any) {
      toast.error(err.message || "Failed to send estimate");
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied!");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Estimate EST-{estimate.estimate_number}</DialogTitle>
          <p className="text-sm text-muted-foreground">{estimate.customers?.name ?? "Unknown Customer"}</p>
        </DialogHeader>

        {shareUrl ? (
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg border bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 text-center space-y-3">
              <p className="font-semibold text-emerald-800 dark:text-emerald-400">✅ Estimate Sent!</p>
              <p className="text-sm text-muted-foreground">Share this link with your customer:</p>
              <div className="flex gap-2">
                <Input value={shareUrl} readOnly className="text-xs font-mono" />
                <Button variant="outline" size="sm" onClick={copyLink}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <Button variant="outline" size="sm" asChild>
                <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                  Preview <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              </Button>
            </div>
            <Button className="w-full" onClick={() => onOpenChange(false)}>Done</Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="space-y-3">
              <Label className="font-medium">Send To</Label>
              <RadioGroup value={sendTo} onValueChange={(v) => setSendTo(v as any)}>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="primary" id="primary" />
                  <Label htmlFor="primary" className="text-sm font-normal">
                    Primary contact {primaryContact ? `(${primaryContact.name})` : "(none set)"}
                  </Label>
                </div>
                {estimateContacts.length > 0 && (
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="all" id="all" />
                    <Label htmlFor="all" className="text-sm font-normal">
                      All contacts who receive estimates ({estimateContacts.length})
                    </Label>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="custom" id="custom" />
                  <Label htmlFor="custom" className="text-sm font-normal">Custom email</Label>
                </div>
              </RadioGroup>

              {sendTo === "custom" && (
                <Input placeholder="email@company.com" value={customEmail} onChange={(e) => setCustomEmail(e.target.value)} />
              )}
            </div>

            <div>
              <Label className="text-sm font-medium">Contact Name</Label>
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Recipient name" />
            </div>

            <div>
              <Label className="text-sm font-medium">Personal Message (optional)</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Hi, please review our estimate..." rows={3} />
            </div>

            <div>
              <Label className="text-sm font-medium">Estimate expires in</Label>
              <div className="flex items-center gap-2">
                <Input type="number" value={expiryDays} onChange={(e) => setExpiryDays(e.target.value)} className="w-20" min="1" />
                <span className="text-sm text-muted-foreground">days from today</span>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button className="flex-1" onClick={handleSend} disabled={sending}>
                {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                Send Estimate
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
